import store from '@/state/store';
import { LOCALES, PUBLIC_PAGES } from '@/helpers/publicPages';

/**
 * The component behind each public page.
 *
 * Kept here rather than in publicPages.js because that table is also read by
 * scripts/generate-seo.js from plain Node, where `import()` of a .vue file is
 * meaningless.
 */
const PUBLIC_PAGE_COMPONENTS = {
  home: () => import('../views/pages/website/index.vue'),
  about: () => import('../views/pages/website/about.vue'),
  contact: () => import('../views/pages/website/contact.vue'),
};

/**
 * One route per (public page x language). The language lives in the URL so each
 * translation is separately indexable and shareable - see
 * src/helpers/publicPages.js for why, and for the table these are built from.
 */
const publicRoutes = [];
for (const page of PUBLIC_PAGES) {
  for (const locale of LOCALES) {
    const path = page.paths[locale];
    const route = {
      path,
      name: page.routeNames[locale],
      component: PUBLIC_PAGE_COMPONENTS[page.key],
      meta: {
        titleKey: page.titleKey,
        standaloneTitle: true,
        // The URL decides the language of a public page. The router applies it
        // (see router/index.js) before the title is resolved.
        locale,
      },
    };
    // `/en/` is the canonical English home; accept a hand-typed `/en` too.
    if (path.length > 1 && path.endsWith('/')) {
      route.alias = path.slice(0, -1);
    }
    publicRoutes.push(route);
  }
}

export default [
...publicRoutes,
// Authentication routes without /app prefix
{
  path: '/app/login',
  name: 'login',
  component: () => import('../views/pages/account/login.vue'),
  meta: {
    title: "Login",
    beforeResolve(routeTo, routeFrom, next) {
      if (store.getters['auth/loggedIn']) {
        next({ name: 'app-home' });
      } else {
        next();
      }
    },
  },
},
{
  path: '/app/ref/:code',
  component: () => import('../views/pages/referral/referral-landing.vue'),
  meta: { title: "Referral" },
  beforeEnter: async (to, from, next) => {
    const code = to.params.code;
    if (code) {
      try {
        const { referralApi } = await import('@/helpers/referralApi');
        const { REFERRAL_COOKIE, setCookie } = await import('@/utils/cookies');
        const result = await referralApi.resolveCode(code);
        if (result.valid) {
          setCookie(REFERRAL_COOKIE, code, result.cookie_duration_days || 30);
        }
      } catch (e) {
        console.error("Failed to resolve referral code:", e);
      }
    }
    // Full page redirect to avoid SPA layout issues
    window.location.href = '/';
  },
},
{
  // Promo (billing coupon) auto-apply link — mirrors /app/ref/:code above.
  // The component never renders: beforeEnter always hard-redirects.
  path: '/app/promo/:code',
  component: () => import('../views/pages/referral/referral-landing.vue'),
  meta: { title: "Promo" },
  beforeEnter: async (to, from, next) => {
    const code = to.params.code;
    if (code) {
      try {
        const { promoApi, setPromoCookie } = await import('@/helpers/promoApi');
        const result = await promoApi.resolveCode(code);
        if (result.valid) {
          setPromoCookie(result.code || code, result.cookie_duration_days || 30);
        }
      } catch (e) {
        console.error("Failed to resolve promo code:", e);
      }
    }
    // Land them where the promo is actually used: the plans page. Anonymous
    // visitors are forwarded to login by the auth guard with redirectFrom set,
    // so they arrive here right after signing in. Valid or not, never strand.
    next({ name: 'subscription-plans' });
  },
},
{
  path: '/app/register',
  name: 'register',
  component: () => import('../views/pages/account/register.vue'),
  meta: {
    title: "Register",
    beforeResolve(routeTo, routeFrom, next) {
      if (store.getters['auth/loggedIn']) {
        next({ name: 'app-home' });
      } else {
        next();
      }
    },
  },
},
{
  path: '/app/forgot-password',
  name: 'Forgot-password',
  component: () => import('../views/pages/account/forgot-password.vue'),
  meta: {
    title: "Forgot Password",
    beforeResolve(routeTo, routeFrom, next) {
      if (store.getters['auth/loggedIn']) {
        next({ name: 'app-home' });
      } else {
        next();
      }
    },
  },
},
{
  path: '/app/reset-password',
  name: 'reset-password',
  component: () => import('../views/pages/auth/recoverpwd.vue'),
  meta: {
    title: "Reset Password",
    beforeResolve(routeTo, routeFrom, next) {
      if (store.getters['auth/loggedIn']) {
        next({ name: 'app-home' });
      } else {
        next();
      }
    },
  },
},
{
  path: '/app/auth/callback/google',
  name: 'google-callback',
  component: () => import('../views/pages/account/google-callback.vue'),
  meta: {
    title: "Google Authentication",
    beforeResolve(routeTo, routeFrom, next) {
      next();
    },
  },
},
{
  path: '/app/facebook/callback',
  name: 'facebook-callback',
  component: () => import('../components/facebook/FacebookOAuthCallback.vue'),
  meta: {
    title: "Facebook Authentication",
    authRequired: true,
  },
},
{
  path: '/app/logout',
  name: 'logout',
  meta: {
    title: "Logout",
    authRequired: true,
    beforeResolve(routeTo, routeFrom, next) {
      if (process.env.VUE_APP_DEFAULT_AUTH === "server") {
        store.dispatch('auth/logOut');
      }
      const authRequiredOnPreviousRoute = routeFrom.matched.some(
        (route) => route.push('/app/login')
      );
      next(authRequiredOnPreviousRoute ? { name: 'app-home' } : { ...routeFrom });
    },
  },
},
// App routes
{
  path: '/app',
  name: 'app-home',
  meta: {
    title: "Dashboard",
    authRequired: true,
  },
  component: () => import('../views/pages/dashboard/dashboard.vue')
},
{
  path: '/app/getting-started',
  name: 'getting-started',
  meta: {
    title: "Getting Started",
    authRequired: true,
  },
  component: () => import('../views/pages/getting-started.vue')
},
{
  path: '/app/products',
  name: 'Products',
  meta: { title: "Products", authRequired: true },
  component: () => import('../views/pages/products/products-list.vue')
},
{
  path: '/app/products/discover/aliexpress',
  name: 'DiscoverAliExpress',
  meta: { title: "Discover AliExpress", authRequired: true },
  component: () => import('../views/pages/products/aliexpress-discover.vue')
},
{
  path: '/app/products/edit/:id',
  name: 'EditProduct',
  meta: { title: "Edit Product", authRequired: true, listRoute: 'Products' },
  component: () => import('../views/pages/products/edit-product.vue')
},
{
  path: '/app/products/import-jobs',
  name: 'ImportJobs',
  meta: { title: "Import Jobs", authRequired: true },
  component: () => import('../views/pages/products/import-jobs.vue')
},
{
  path: '/app/tools/affiliate-link-generator',
  name: 'AffiliateLinkGenerator',
  meta: { title: "Affiliate Link Generator", authRequired: true },
  component: () => import('../views/pages/tools/affiliate-link-generator.vue')
},
{
  path: '/app/groups',
  name: 'Groups',
  meta: { title: "All Groups", authRequired: true },
  component: () => import('../views/pages/groups/group-list.vue')
},
{
  path: '/app/scheduled-messages',
  name: 'scheduled-messages-list',
  meta: { title: 'Scheduled Messages', authRequired: true },
  component: () => import('../views/pages/scheduled-messages/scheduled-messages-list.vue')
},
{
  path: '/app/scheduled-messages/new',
  name: 'scheduled-messages-new',
  meta: { title: 'New Scheduled Message', authRequired: true, listRoute: 'scheduled-messages-list' },
  component: () => import('../views/pages/scheduled-messages/scheduled-messages-edit.vue')
},
{
  path: '/app/scheduled-messages/:id',
  name: 'scheduled-messages-edit',
  meta: { title: 'Edit Scheduled Message', authRequired: true, listRoute: 'scheduled-messages-list' },
  component: () => import('../views/pages/scheduled-messages/scheduled-messages-edit.vue')
},

{
  path: '/app/activity',
  name: 'Activity',
  meta: { title: "Activity Dashboard", authRequired: true },
  component: () => import('../views/pages/activity/index.vue')
},
{
  path: '/app/analytics',
  name: 'Analytics',
  meta: { title: "Analytics", authRequired: true },
  component: () => import('../views/pages/analytics/index.vue')
},
{
  path: '/app/utility/starter',
  name: 'Starter-page',
  meta: { title: "Starter page", authRequired: true },
  component: () => import('../views/pages/utility/starter.vue')
},
{
  path: '/app/utility/maintenance',
  name: 'maintenance',
  meta: { title: "Maintenance", authRequired: true },
  component: () => import('../views/pages/utility/maintenance.vue')
},
{
  path: '/app/utility/comingsoon',
  name: 'comingsoon',
  meta: { title: "Comingsoon", authRequired: true },
  component: () => import('../views/pages/utility/comingsoon.vue')
},
{
  path: '/app/auth/confirm',
  name: 'confirm',
  component: () => import('../views/pages/account/confirm.vue'),
  meta: {
    beforeResolve(routeTo, routeFrom, next) {
      if (!routeTo.query.access_token) {
        next({ name: 'login' })
      } else {
        next()
      }
    },
  },
},
{
  path: '/app/account/profile',
  name: 'profile',
  component: () => import('../views/pages/account/profile.vue'),
  meta: {
    title: "Profile",
    authRequired: true,
  },
},
{
  path: '/app/settings/community',
  name: 'community-settings',
  component: () => import('../views/pages/settings/community-settings.vue'),
  meta: {
    title: "Community Settings",
    authRequired: true,
  },
},
{
  path: '/app/settings/community/add',
  name: 'add-community',
  component: () => import('../views/pages/settings/add-community.vue'),
  meta: {
    title: "Add Community",
    authRequired: true,
  },
},
{
  path: '/app/settings/message-templates',
  name: 'MessageTemplates',
  component: () => import('../views/pages/settings/message-templates.vue'),
  meta: {
    title: 'Message Templates',
    authRequired: true,
  }
},
{
  path: '/app/settings/ai-message-structure',
  name: 'AIMessageStructure',
  component: () => import('../views/pages/settings/ai-message-structure.vue'),
  meta: {
    title: 'AI Message Structure',
    authRequired: true,
  }
},
{
  path: '/app/settings/whatsapp-instances',
  name: 'WhatsAppInstances',
  component: () => import('../views/pages/settings/whatsapp-instances.vue'),
  meta: {
    title: 'WhatsApp Instances',
    authRequired: true,
  }
},
{
  path: '/app/coupons',
  name: 'Coupons',
  component: () => import('../views/pages/coupons/coupons.vue'),
  meta: {
    title: 'Coupons',
    authRequired: true,
  }
},
{
  path: '/app/documentation/overview',
  name: 'documentation-overview',
  component: () => import('../views/pages/documentation/overview.vue'),
  meta: {
    title: "Documentation",
    authRequired: true,
  }
},
{
  path: '/app/documentation/telegram-group',
  name: 'documentation-telegram-group',
  component: () => import('../views/pages/documentation/telegram-group.vue'),
  meta: {
    title: "Create Telegram Group",
    authRequired: true,
  }
},
{
  path: '/app/documentation/whatsapp-group',
  name: 'documentation-whatsapp-group',
  component: () => import('../views/pages/documentation/whatsapp-group.vue'),
  meta: {
    title: "Create WhatsApp Group",
    authRequired: true,
  }
},
{
  path: '/app/documentation/product-demo',
  name: 'product-demo',
  component: () => import('../views/pages/documentation/ProductDemo.vue'),
  meta: {
    title: "Product Demo",
    authRequired: true,
  }
},
{
  path: '/app/onboarding',
  name: 'onboarding',
  component: () => import('../views/pages/account/onboarding.vue'),
  meta: {
    title: "Welcome to Affiloop.io",
    authRequired: true,
    beforeResolve(routeTo, routeFrom, next) {
      if (!store.getters['auth/loggedIn']) {
        next({ name: 'login' });
        return;
      }

      // Need to fetch the user profile first to check onboarding status
      // But only fetch it if we haven't already
      if (!store.state.auth.currentUser) {
        store.dispatch('auth/fetchProfile')
          .then(profile => {
            if (profile && profile.completed_onboarding) {
              next({ name: 'app-home' });
            } else {
              next();
            }
          })
          .catch(() => {
            // If we can't fetch the profile, redirect to home to prevent loops
            next({ name: 'app-home' });
          });
      } else if (store.getters['auth/hasCompletedOnboarding']) {
        // If onboarding is completed, redirect to home
        next({ name: 'app-home' });
      } else {
        next();
      }
    }
  }
},
{
  path: '/app/orders',
  name: 'Orders',
  meta: { title: "Orders", authRequired: true },
  component: () => import('../views/pages/orders/orders-list.vue')
},
{
  path: '/app/orders/by-product',
  name: 'orders-by-product',
  component: () => import('../views/pages/orders/by-product.vue'),
  meta: {
    authRequired: true,
    title: "Top Products"
  }
},
{
  path: "/app/coming-soon/:feature?",
  name: "coming-soon",
  component: () => import("../views/pages/coming-soon.vue"),
  meta: { requiresAuth: true },
},
// Admin routes

{
  path: '/app/admin',
  component: () => import('../views/layouts/admin.vue'),
  meta: {
    title: "Admin Panel",
    authRequired: true,
    beforeResolve(routeTo, routeFrom, next) {
      // Check if user is admin
      if (!store.getters['auth/user']?.is_admin) {
        // 'app-home' (not 'home', which doesn't exist) — matches the v2 admin guard
        next({ name: 'app-home' });
      } else {
        next();
      }
    },
  },
  children: [
    {
      path: '',
      redirect: { name: 'admin-users' }
    },
    {
      path: 'users',
      name: 'admin-users',
      component: () => import('../views/pages/admin/users.vue'),
      meta: {
        title: "Users Management"
      }
    },
    {
      path: 'communities',
      name: 'admin-communities',
      component: () => import('../views/pages/admin/communities.vue'),
      meta: {
        title: "Communities Management"
      }
    },
    {
      path: 'whatsapp',
      name: 'admin-whatsapp',
      component: () => import('../views/pages/admin/AdminWhatsApp.vue'),
      meta: {
        title: "WhatsApp Instances"
      }
    },
    {
      path: 'settings',
      name: 'admin-settings',
      component: () => import('../views/pages/admin/AdminSettings.vue'),
      meta: {
        title: "App Settings"
      }
    },
    {
      path: 'groups',
      name: 'admin-groups',
      component: () => import('../views/pages/admin/AdminGroups.vue'),
      meta: {
        title: "Groups Management"
      }
    },
    {
      path: 'products',
      name: 'admin-products',
      component: () => import('../views/pages/admin/AdminProducts.vue'),
      meta: {
        title: "Products Management"
      }
    },
    {
      path: 'tasks',
      name: 'admin-tasks',
      component: () => import('../views/pages/admin/tasks.vue'),
      meta: {
        title: "Tasks"
      }
    },
    {
      path: 'subscriptions',
      name: 'admin-subscriptions',
      component: () => import('../views/pages/admin/subscriptions.vue'),
      meta: {
        title: "Subscription Management"
      }
    },
    {
      path: 'custom-plans',
      name: 'admin-custom-plans',
      component: () => import('../views/pages/admin/custom-plans.vue'),
      meta: {
        title: "Custom Plan Management"
      }
    },
    {
      path: 'system-templates',
      name: 'admin-system-templates',
      component: () => import('../views/pages/admin/system-templates.vue'),
      meta: {
        title: "System Templates"
      }
    }
  ]
},
// Admin V2 - Modern Admin Interface
{
  path: '/app/admin-v2',
  component: () => import('../views/admin-v2/layouts/AdminV2Layout.vue'),
  meta: {
    title: "Admin Portal V2",
    authRequired: true,
    beforeResolve(routeTo, routeFrom, next) {
      // Check if user is admin
      if (!store.getters['auth/user']?.is_admin) {
        next({ name: 'app-home' });
      } else {
        next();
      }
    },
  },
  children: [
    {
      path: '',
      redirect: { name: 'admin-v2-customers' }
    },
    {
      path: 'customers',
      name: 'admin-v2-customers',
      component: () => import('../views/admin-v2/screens/customers/CustomerHub.vue'),
      meta: { title: "Customer Hub" }
    },
    {
      path: 'customers/:id',
      name: 'admin-v2-customer-detail',
      component: () => import('../views/admin-v2/screens/customers/CustomerDetail.vue'),
      meta: { title: "Customer Details" }
    },
    {
      path: 'auth-events',
      name: 'admin-v2-auth-events',
      component: () => import('../views/admin-v2/screens/customers/AuthEventsLog.vue'),
      meta: { title: "Auth Events" }
    },
    {
      path: 'communities',
      name: 'admin-v2-communities',
      component: () => import('../views/admin-v2/screens/communities/CommunitiesOverview.vue'),
      meta: { title: "Communities" }
    },
    {
      path: 'subscriptions',
      name: 'admin-v2-subscriptions',
      component: () => import('../views/admin-v2/screens/subscriptions/SubscriptionManager.vue'),
      meta: { title: "Subscriptions" }
    },
    {
      path: 'subscriptions/plans',
      name: 'admin-v2-plans',
      component: () => import('../views/admin-v2/screens/subscriptions/PlansManager.vue'),
      meta: { title: "Plans" }
    },
    {
      path: 'subscriptions/custom-plans',
      name: 'admin-v2-custom-plans',
      component: () => import('../views/admin-v2/screens/subscriptions/CustomPlans.vue'),
      meta: { title: "Custom Plans" }
    },
    {
      path: 'products',
      name: 'admin-v2-products',
      component: () => import('../views/admin-v2/screens/products/ProductsOverview.vue'),
      meta: { title: "Products" }
    },
    {
      path: 'user-products',
      name: 'admin-v2-user-products',
      component: () => import('../views/admin-v2/screens/user-products/UserProductsOverview.vue'),
      meta: { title: "User Products" }
    },
    {
      path: 'top-products',
      name: 'admin-v2-top-products',
      component: () => import('../views/admin-v2/screens/orders/TopProducts.vue'),
      meta: { title: "Top Selling Products" }
    },
    {
      path: 'groups',
      name: 'admin-v2-groups',
      component: () => import('../views/admin-v2/screens/groups/GroupsOverview.vue'),
      meta: { title: "Groups" }
    },
    {
      path: 'whatsapp',
      name: 'admin-v2-whatsapp',
      component: () => import('../views/admin-v2/screens/whatsapp/InstanceManager.vue'),
      meta: { title: "WhatsApp Management" }
    },
    {
      path: 'whatsapp/pool',
      name: 'admin-v2-whapi-pool',
      component: () => import('../views/admin-v2/screens/whatsapp/WhapiPoolManager.vue'),
      meta: { title: "WHAPI Pool" }
    },
    {
      path: 'tasks',
      name: 'admin-v2-tasks',
      component: () => import('../views/admin-v2/screens/tasks/TaskQueue.vue'),
      meta: { title: "Task Queue" }
    },
    {
      path: 'stats',
      name: 'admin-v2-stats',
      component: () => import('../views/admin-v2/screens/stats/SystemStats.vue'),
      meta: { title: "System Stats" }
    },
    {
      path: 'business-metrics',
      name: 'admin-v2-business-metrics',
      component: () => import('../views/admin-v2/screens/stats/BusinessMetrics.vue'),
      meta: { title: "Business Metrics" }
    },
    {
      path: 'evolution',
      name: 'admin-v2-evolution-dashboard',
      component: () => import('../views/admin-v2/screens/evolution/EvolutionDashboard.vue'),
      meta: { title: "Evolution Overview" }
    },
    {
      path: 'evolution/servers',
      name: 'admin-v2-evolution-servers',
      component: () => import('../views/admin-v2/screens/evolution/EvolutionServers.vue'),
      meta: { title: "Evolution Servers" }
    },
    {
      path: 'evolution/proxies',
      name: 'admin-v2-evolution-proxies',
      component: () => import('../views/admin-v2/screens/evolution/EvolutionProxies.vue'),
      meta: { title: "Evolution Proxies" }
    },
    {
      path: 'evolution/proxy-monitor',
      name: 'admin-v2-evolution-proxy-monitor',
      component: () => import('../views/admin-v2/screens/evolution/EvolutionProxyMonitor.vue'),
      meta: { title: "Proxy Monitor" }
    },
    {
      path: 'referrals',
      name: 'admin-v2-referrals',
      component: () => import('../views/admin-v2/screens/referrals/AdminReferrals.vue'),
      meta: { title: "Referral Management" }
    },
    {
      path: 'promo-codes',
      name: 'admin-v2-promo-codes',
      component: () => import('../views/admin-v2/screens/promo/AdminPromoCodes.vue'),
      meta: { title: "Promo Codes" }
    }
  ]
},
{
  path: '/app/referrals',
  name: 'referrals',
  component: () => import('../views/pages/referral/referrals.vue'),
  meta: {
    title: "Referrals",
    authRequired: true,
  },
},
{
  path: '/app/subscription',
  component: () => import('../views/pages/subscription/subscription-layout.vue'),
  meta: {
    authRequired: true,
  },
  children: [
    {
      path: '',
      name: 'subscription-overview',
      component: () => import('../views/pages/subscription/subscription-overview.vue'),
      meta: { title: "Subscription Overview" },
    },
    {
      path: 'plans',
      name: 'subscription-plans',
      component: () => import('../views/pages/subscription/subscription-plans.vue'),
      meta: { title: "Subscription Plans" },
    },
    {
      path: 'manage',
      name: 'subscription-manage',
      component: () => import('../views/pages/subscription/subscription-manage.vue'),
      meta: { title: "Manage Subscription" },
    },
    {
      path: 'success',
      name: 'subscription-success',
      component: () => import('../views/pages/subscription/subscription-success.vue'),
      meta: { title: "Subscription Success" },
    },
    {
      path: 'cancel',
      name: 'subscription-cancel',
      component: () => import('../views/pages/subscription/subscription-cancel.vue'),
      meta: { title: "Subscription Canceled" },
    },
  ]
},
{
  path: '/app/payment/success',
  name: 'payment-success',
  meta: { title: "Payment Success", authRequired: true },
  component: () => import('../views/pages/payment/success.vue')
},
];